import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Admin,
  ProfileUpdate,
  clearToken,
  fetchMe,
  getToken,
  login as loginRequest,
  setToken,
  updateProfile as updateProfileRequest,
} from '../services/auth';

interface AuthContextValue {
  admin: Admin | null;
  /** True until the stored token has been checked against the server. */
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  saveProfile: (update: ProfileUpdate) => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore the session on load. A stored token can be expired or revoked, so
  // it is only trusted once /me confirms it.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const me = await fetchMe();
        if (!cancelled) setAdmin(me);
      } catch {
        clearToken();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { token, admin: signedIn } = await loginRequest(email, password);
    setToken(token);
    setAdmin(signedIn);
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    setAdmin(null);
  }, []);

  const saveProfile = useCallback(async (update: ProfileUpdate) => {
    const updated = await updateProfileRequest(update);
    setAdmin(updated);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setAdmin(await fetchMe());
    } catch {
      clearToken();
      setAdmin(null);
    }
  }, []);

  const value = useMemo(
    () => ({ admin, loading, signIn, signOut, saveProfile, refresh }),
    [admin, loading, signIn, signOut, saveProfile, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
};
